import HelpPopover from '@components/AppNavigation/HelpPopover';
import UserButtonsMenu from '@components/AppNavigation/UserButtonsMenu';
import ChatBubbleProvider from '@components/DevRev/SupportBubbleProvider';
import DescopeLogo from '@components/Logo/DescopeLogo';
import ProjectNotifications from '@components/ProjectNotifications/ProjectNotifications';
import MenuIcon from '@mui/icons-material/Menu';
import {
	Alert,
	AppBar,
	Divider,
	ToggleButton,
	Typography
} from '@mui/material';
import Box from '@mui/material/Box';
import { grey } from '@mui/material/colors';
import MuiDrawer from '@mui/material/Drawer';
import { CSSObject, styled, Theme } from '@mui/material/styles';
import { useTheme } from '@mui/system';
import {
	SIDE_BAR_WIDTH,
	TOP_PANE_BAR_HEIGHT,
	TOP_PANE_BAR_LOW_RES_ALERT_HEIGHT
} from 'constants/Theme/BaseTheme';
import { useLowResolution } from 'hooks';
import React, { FC, useState } from 'react';
import AuthenticatedAppRoutes from 'routes/AuthenticatedAppRoutes';
import { IThemeChangeProps } from 'types/theme';
import ProjectHeader from '../Settings/ProjectSettings/ProjectHeader';
import MainLayout from './MainLayout';
import NavigationMenu from './NavigationMenu';

const openedMixin = (theme: Theme): CSSObject => ({
	width: SIDE_BAR_WIDTH,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen
	}),
	overflowX: 'hidden'
});

const openedAppBar = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen
	}),
	overflowX: 'hidden'
});

const closedMixin = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen
	}),
	overflowX: 'hidden',
	width: `calc(${theme.spacing(10)} + 2px)`
});

const Drawer = styled(MuiDrawer, {
	shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
	width: SIDE_BAR_WIDTH,
	flexShrink: 0,
	whiteSpace: 'nowrap',
	boxSizing: 'border-box',
	...(open && {
		...openedMixin(theme),
		'& .MuiDrawer-paper': {
			...openedMixin(theme),
			backgroundColor: theme.palette.mode === 'dark' ? grey[900] : grey[50]
		}
	}),
	...(!open && {
		...closedMixin(theme),
		'& .MuiDrawer-paper': {
			...closedMixin(theme),
			backgroundColor: theme.palette.mode === 'dark' ? grey[900] : grey[50]
		}
	})
}));

const AppContainer: FC<IThemeChangeProps> = ({
	isDarkTheme,
	onThemeChange
}) => {
	const theme = useTheme<Theme>();
	const lowRes = useLowResolution();
	const [open, setOpen] = useState(!lowRes);

	const appBarHeight =
		TOP_PANE_BAR_HEIGHT + (lowRes ? TOP_PANE_BAR_LOW_RES_ALERT_HEIGHT : 0);

	const fullNavMenu = lowRes && open;

	return (
		<Box
			display="flex"
			flexDirection="row"
			flexGrow={1}
			id="app-container-main"
		>
			<ChatBubbleProvider>
				<>
					<AppBar
						position="fixed"
						color="transparent"
						elevation={0}
						sx={{
							height: appBarHeight,
							backgroundColor:
								theme.palette.mode === 'dark' ? grey[900] : grey[50],
							...openedAppBar(theme)
						}}
					>
						<Box display="flex" flexDirection="row" flexGrow={1}>
							<Box
								display="flex"
								flexGrow={1}
								flexDirection="row"
								alignItems="center"
							>
								<Box
									display="flex"
									flexGrow={1}
									position="absolute"
									left={0}
									right={0}
									ml={8}
								/>
								<ToggleButton
									onChange={() => setOpen(!open)}
									selected={!open}
									color="standard"
									value="check"
									size="small"
									aria-label={
										open ? intl.tr('button.close') : intl.tr('button.open')
									}
									title={
										open ? intl.tr('button.close') : intl.tr('button.open')
									}
									sx={{
										marginLeft: '13px',
										borderRadius: '50%',
										border: 0,
										opacity: 0.8
									}}
									data-testid="app-container-drawer-button"
								>
									<MenuIcon fontSize="medium" />
								</ToggleButton>
								<DescopeLogo width={100} navigation />
							</Box>
							<Box
								display="flex"
								flexGrow={1}
								flexDirection="row"
								justifyContent="flex-end"
								alignItems="center"
								pr={{
									xs: 0,
									sm: 4
								}}
							>
								<ProjectNotifications />
								<HelpPopover />
								<UserButtonsMenu
									isDarkTheme={isDarkTheme}
									onThemeChange={onThemeChange}
								/>
							</Box>
						</Box>
						{lowRes && (
							<Alert
								severity="info"
								sx={{
									height: TOP_PANE_BAR_LOW_RES_ALERT_HEIGHT,
									py: 0,
									px: 1,
									borderRadius: 0,
									display: 'flex',
									alignItems: 'center',
									lineBreak: 'auto'
								}}
							>
								<Typography
									variant="caption"
									title={intl.tr('app.header.mobile.disclaimer')}
								>
									{intl.tr('app.header.mobile.disclaimer')}
								</Typography>
							</Alert>
						)}
						<Divider />
					</AppBar>
					{(!lowRes || open) && (
						<Drawer
							variant="permanent"
							open={open}
							id="app-drawer"
							sx={{
								position: 'sticky',
								'& .MuiDrawer-paper': {
									borderRight: 0,
									width: fullNavMenu ? '100%' : 'inherited'
								}
							}}
						>
							<Box pr={1} pl={1} pt={`${appBarHeight}px`}>
								<ProjectHeader drawerOpen={open} />
							</Box>
							<Box px={2}>
								<Divider />
							</Box>
							<Box data-testid="navigation-menu-list-box" width="100%" px={2}>
								<NavigationMenu
									isOpen={open}
									onClick={() => {
										if (lowRes) {
											setOpen(false);
										}
									}}
								/>
							</Box>
						</Drawer>
					)}
					<MainLayout
						sx={{
							mt: `${appBarHeight}px`,
							display: fullNavMenu ? 'none' : 'inherit'
						}}
					>
						<AuthenticatedAppRoutes />
					</MainLayout>
				</>
			</ChatBubbleProvider>
		</Box>
	);
};

export default AppContainer;
